import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageSeo, buildJsonLd, canonicalUrl, faqSchema, OG_IMAGE } from '@/data/seo';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Route-driven <head> manager. Mounted once in <Layout>. The prerender step
 * (scripts/prerender.js) snapshots the resulting <head> into static HTML so
 * crawlers see real titles, descriptions, canonicals and JSON-LD.
 */
export function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getPageSeo(pathname);
    const url = canonicalUrl(seo.path);

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', seo.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'NioDelta');
    upsertMeta('property', 'og:locale', 'en_IN');
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', OG_IMAGE);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', OG_IMAGE);

    upsertJsonLd('ld-page', seo.noindex ? null : buildJsonLd(seo));
  }, [pathname]);

  return null;
}

/** Page-level FAQPage JSON-LD; pass the same items the FAQ list renders. */
export function FaqSchema({ items }) {
  useEffect(() => {
    upsertJsonLd('ld-faq', faqSchema(items));
    return () => upsertJsonLd('ld-faq', null);
  }, [items]);
  return null;
}
